"use client"

import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import {
    Box,
    Input,
    Button,
    FormControl,
    FormLabel,
    VStack,
    Text,
    InputGroup,
    InputRightElement,
    useColorModeValue,
    useToast,
    Progress,
    List,
    ListItem,
    ListIcon,
    HStack,
    Icon,
    Collapse,
    ScaleFade,
} from "@chakra-ui/react"
import { ChangePasswordAsync } from "../Slice/PasswordChange/PasswordChangeSlice"
import { useNavigate } from "react-router-dom"
import "./Login.css"

// Heroicons v2
const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
        <path
            fillRule="evenodd"
            d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
            clipRule="evenodd"
        />
    </svg>
)

const EyeSlashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
        <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
        <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
    </svg>
)

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
    </svg>
)

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
    </svg>
)

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clipRule="evenodd" />
    </svg>
)

// Password validation helper
const validatePassword = (password) => {
    const validations = {
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }

    const strength = Object.values(validations).filter(Boolean).length
    return { validations, strength }
}

const getStrengthLabel = (strength) => {
    switch (strength) {
        case 0:
        case 1:
            return { label: "Very Weak", color: "red" }
        case 2:
            return { label: "Weak", color: "orange" }
        case 3:
            return { label: "Fair", color: "yellow" }
        case 4:
            return { label: "Strong", color: "blue" }
        case 5:
            return { label: "Very Strong", color: "green" }
        default:
            return { label: "", color: "gray" }
    }
}

const UpdatePasswordForm = () => {
    const dispatch = useDispatch()
    const toast = useToast()
    const navigate = useNavigate()

    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [loading, setLoading] = useState(false)
    const [touched, setTouched] = useState({
        current: false,
        new: false,
        confirm: false,
    })

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    })

    const [passwordValidation, setPasswordValidation] = useState({
        validations: {
            minLength: false,
            hasUppercase: false,
            hasLowercase: false,
            hasNumber: false,
            hasSpecialChar: false,
        },
        strength: 0,
    })

    const [showRequirements, setShowRequirements] = useState(false)

    // Update password validation when new password changes
    useEffect(() => {
        const result = validatePassword(newPassword)
        setPasswordValidation(result)
    }, [newPassword])

    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }))
    }

    const handleBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }))
    }

    const handleFocus = (field) => {
        if (field === "new") {
            setShowRequirements(true)
        }
    }

    const passwordsMatch = newPassword === confirmPassword && confirmPassword !== ""
    const passwordsDontMatch = newPassword !== confirmPassword && confirmPassword !== "" && touched.confirm
    const isFormValid =
        currentPassword !== "" &&
        newPassword !== "" &&
        confirmPassword !== "" &&
        passwordsMatch &&
        passwordValidation.strength >= 4

    const strengthInfo = getStrengthLabel(passwordValidation.strength)

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Mark all fields as touched
        setTouched({ current: true, new: true, confirm: true })

        if (!currentPassword) {
            toast({
                title: "Current password required",
                description: "Please enter your current password",
                status: "warning",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            })
            return
        }

        if (passwordValidation.strength < 4) {
            toast({
                title: "Weak Password",
                description: "Please create a stronger password that meets all requirements",
                status: "warning",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            })
            return
        }

        if (newPassword !== confirmPassword) {
            toast({
                title: "Passwords don't match",
                description: "New password and confirm password must be the same",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            })
            return
        }

        if (currentPassword === newPassword) {
            toast({
                title: "Same Password",
                description: "New password must be different from current password",
                status: "warning",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            })
            return
        }

        setLoading(true)

        try {
            await dispatch(
                ChangePasswordAsync({
                    current_password: currentPassword,
                    password: newPassword,
                    password_confirmation: confirmPassword,
                })
            ).unwrap()

            toast({
                title: "Password Updated! 🎉",
                description: "Your password has been changed successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            })

            // Clear form
            setCurrentPassword("")
            setNewPassword("")
            setConfirmPassword("")
            setTouched({ current: false, new: false, confirm: false })

            setTimeout(() => {
                navigate("/")
            }, 1500)
        } catch (error) {
            const errorMessage = error?.message || "Failed to update password. Please try again."
            toast({
                title: "Update Failed",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            })
        } finally {
            setLoading(false)
        }
    }

    // Color mode values
    const cardBg = useColorModeValue("white", "gray.800")
    const inputBg = useColorModeValue("gray.50", "gray.700")
    const borderColor = useColorModeValue("gray.200", "gray.600")
    const labelColor = useColorModeValue("gray.700", "gray.200")
    const requirementsBg = useColorModeValue("gray.50", "gray.700")

    const validationItems = [
        { key: "minLength", label: "At least 8 characters" },
        { key: "hasUppercase", label: "One uppercase letter (A-Z)" },
        { key: "hasLowercase", label: "One lowercase letter (a-z)" },
        { key: "hasNumber", label: "One number (0-9)" },
        { key: "hasSpecialChar", label: "One special character (!@#$%^&*)" },
    ]

    return (
        <div className="">
            <div className="bg-mainBg p-3">
                <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
                    {/* Header */}
                    <div className="sm:px-24 mb-6">
                        <HStack spacing={3} mb={2}>
                            <Box color="blue.500">
                                <LockIcon />
                            </Box>
                            <h1 className="sm:text-4xl text-2xl font-semibold text-left">
                                Change Password
                            </h1>
                        </HStack>
                        <Text color="gray.500" fontSize="sm" ml={9}>
                            Keep your account secure by using a strong password
                        </Text>
                    </div>

                    {/* Form Card */}
                    <div className="rounded-xl sm:px-24">
                        <Box
                            bg={cardBg}
                            borderRadius="xl"
                            boxShadow="sm"
                            border="1px"
                            borderColor={borderColor}
                            overflow="hidden"
                            transition="all 0.2s"
                            _hover={{ boxShadow: "md" }}
                        >
                            <Box p={{ base: 4, md: 8 }}>
                                <form onSubmit={handleSubmit}>
                                    <VStack spacing={6}>
                                        {/* Current Password */}
                                        <FormControl isRequired>
                                            <FormLabel fontWeight="medium" color={labelColor} fontSize="sm">
                                                Current Password
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <Input
                                                    type={showPassword.current ? "text" : "password"}
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    onBlur={() => handleBlur("current")}
                                                    bg={inputBg}
                                                    borderRadius="lg"
                                                    border="1px"
                                                    borderColor={
                                                        touched.current && !currentPassword
                                                            ? "red.300"
                                                            : borderColor
                                                    }
                                                    _hover={{ borderColor: "blue.300" }}
                                                    _focus={{
                                                        borderColor: "blue.500",
                                                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                                                        bg: "white",
                                                    }}
                                                    placeholder="Enter your current password"
                                                    transition="all 0.2s"
                                                />
                                                <InputRightElement h="full">
                                                    <Box
                                                        as="button"
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility("current")}
                                                        color="gray.400"
                                                        _hover={{ color: "gray.600" }}
                                                        p={2}
                                                        transition="color 0.2s"
                                                    >
                                                        {showPassword.current ? <EyeSlashIcon /> : <EyeIcon />}
                                                    </Box>
                                                </InputRightElement>
                                            </InputGroup>
                                            {touched.current && !currentPassword && (
                                                <Text color="red.500" fontSize="xs" mt={1}>
                                                    Current password is required
                                                </Text>
                                            )}
                                        </FormControl>

                                        {/* New Password */}
                                        <FormControl isRequired>
                                            <FormLabel fontWeight="medium" color={labelColor} fontSize="sm">
                                                New Password
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <Input
                                                    type={showPassword.new ? "text" : "password"}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    onFocus={() => handleFocus("new")}
                                                    onBlur={() => handleBlur("new")}
                                                    bg={inputBg}
                                                    borderRadius="lg"
                                                    border="1px"
                                                    borderColor={
                                                        touched.new && passwordValidation.strength < 4
                                                            ? "orange.300"
                                                            : passwordValidation.strength >= 4
                                                                ? "green.300"
                                                                : borderColor
                                                    }
                                                    _hover={{ borderColor: "blue.300" }}
                                                    _focus={{
                                                        borderColor: "blue.500",
                                                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                                                        bg: "white",
                                                    }}
                                                    placeholder="Create a strong password"
                                                    transition="all 0.2s"
                                                />
                                                <InputRightElement h="full">
                                                    <Box
                                                        as="button"
                                                        type="button"
                                                        onClick={() => togglePasswordVisibility("new")}
                                                        color="gray.400"
                                                        _hover={{ color: "gray.600" }}
                                                        p={2}
                                                        transition="color 0.2s"
                                                    >
                                                        {showPassword.new ? <EyeSlashIcon /> : <EyeIcon />}
                                                    </Box>
                                                </InputRightElement>
                                            </InputGroup>

                                            {/* Password Strength Indicator */}
                                            <Collapse in={newPassword.length > 0} animateOpacity>
                                                <Box mt={3}>
                                                    <HStack justify="space-between" mb={1}>
                                                        <Text fontSize="xs" color="gray.500">
                                                            Password Strength
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            fontWeight="semibold"
                                                            color={`${strengthInfo.color}.500`}
                                                        >
                                                            {strengthInfo.label}
                                                        </Text>
                                                    </HStack>
                                                    <Progress
                                                        value={(passwordValidation.strength / 5) * 100}
                                                        size="sm"
                                                        colorScheme={strengthInfo.color}
                                                        borderRadius="full"
                                                        bg="gray.100"
                                                        transition="all 0.3s"
                                                    />
                                                </Box>
                                            </Collapse>

                                            {/* Password Requirements */}
                                            <Collapse in={showRequirements && newPassword.length > 0} animateOpacity>
                                                <Box
                                                    mt={4}
                                                    p={4}
                                                    bg={requirementsBg}
                                                    borderRadius="lg"
                                                    border="1px"
                                                    borderColor={borderColor}
                                                >
                                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.600">
                                                        Password Requirements:
                                                    </Text>
                                                    <List spacing={1}>
                                                        {validationItems.map((item) => (
                                                            <ListItem
                                                                key={item.key}
                                                                display="flex"
                                                                alignItems="center"
                                                                fontSize="xs"
                                                            >
                                                                <Box
                                                                    mr={2}
                                                                    color={
                                                                        passwordValidation.validations[item.key]
                                                                            ? "green.500"
                                                                            : "gray.400"
                                                                    }
                                                                    transition="color 0.2s"
                                                                >
                                                                    {passwordValidation.validations[item.key] ? (
                                                                        <CheckIcon />
                                                                    ) : (
                                                                        <XIcon />
                                                                    )}
                                                                </Box>
                                                                <Text
                                                                    color={
                                                                        passwordValidation.validations[item.key]
                                                                            ? "green.600"
                                                                            : "gray.500"
                                                                    }
                                                                    textDecoration={
                                                                        passwordValidation.validations[item.key]
                                                                            ? "line-through"
                                                                            : "none"
                                                                    }
                                                                    transition="all 0.2s"
                                                                >
                                                                    {item.label}
                                                                </Text>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            </Collapse>
                                        </FormControl>

                                        {/* Confirm Password */}
                                        <FormControl isRequired>
                                            <FormLabel fontWeight="medium" color={labelColor} fontSize="sm">
                                                Confirm New Password
                                            </FormLabel>
                                            <InputGroup size="lg">
                                                <Input
                                                    type={showPassword.confirm ? "text" : "password"}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    onBlur={() => handleBlur("confirm")}
                                                    bg={inputBg}
                                                    borderRadius="lg"
                                                    border="1px"
                                                    borderColor={
                                                        passwordsDontMatch
                                                            ? "red.300"
                                                            : passwordsMatch
                                                                ? "green.300"
                                                                : borderColor
                                                    }
                                                    _hover={{ borderColor: "blue.300" }}
                                                    _focus={{
                                                        borderColor: "blue.500",
                                                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                                                        bg: "white",
                                                    }}
                                                    placeholder="Confirm your new password"
                                                    transition="all 0.2s"
                                                />
                                                <InputRightElement h="full">
                                                    <HStack spacing={1}>
                                                        {confirmPassword && (
                                                            <ScaleFade in={true} initialScale={0.5}>
                                                                <Box
                                                                    color={passwordsMatch ? "green.500" : "red.500"}
                                                                >
                                                                    {passwordsMatch ? <CheckIcon /> : <XIcon />}
                                                                </Box>
                                                            </ScaleFade>
                                                        )}
                                                        <Box
                                                            as="button"
                                                            type="button"
                                                            onClick={() => togglePasswordVisibility("confirm")}
                                                            color="gray.400"
                                                            _hover={{ color: "gray.600" }}
                                                            p={2}
                                                            transition="color 0.2s"
                                                        >
                                                            {showPassword.confirm ? <EyeSlashIcon /> : <EyeIcon />}
                                                        </Box>
                                                    </HStack>
                                                </InputRightElement>
                                            </InputGroup>
                                            {passwordsDontMatch && (
                                                <Text color="red.500" fontSize="xs" mt={1}>
                                                    Passwords do not match
                                                </Text>
                                            )}
                                            {passwordsMatch && (
                                                <Text color="green.500" fontSize="xs" mt={1}>
                                                    ✓ Passwords match
                                                </Text>
                                            )}
                                        </FormControl>

                                        {/* Submit Button */}
                                        <Button
                                            type="submit"
                                            isLoading={loading}
                                            loadingText="Updating Password..."
                                            width="full"
                                            size="lg"
                                            bg={isFormValid ? "blue.500" : "gray.300"}
                                            color="white"
                                            _hover={{
                                                bg: isFormValid ? "blue.600" : "gray.300",
                                                transform: isFormValid ? "translateY(-2px)" : "none",
                                                boxShadow: isFormValid ? "lg" : "none",
                                            }}
                                            _active={{
                                                bg: isFormValid ? "blue.700" : "gray.300",
                                                transform: "translateY(0)",
                                            }}
                                            _disabled={{
                                                bg: "gray.300",
                                                cursor: "not-allowed",
                                            }}
                                            transition="all 0.2s"
                                            borderRadius="lg"
                                            fontWeight="semibold"
                                            mt={2}
                                        >
                                            {loading ? "Updating..." : "Update Password"}
                                        </Button>

                                        {/* Security Note */}
                                        <Box
                                            w="full"
                                            p={4}
                                            bg="blue.50"
                                            borderRadius="lg"
                                            border="1px"
                                            borderColor="blue.100"
                                        >
                                            <HStack spacing={2} alignItems={"start"}>
                                                <Box color="blue.500" flexShrink={0}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                                        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                                                    </svg>
                                                </Box>
                                                <Text fontSize="xs" color="blue.700">
                                                    For your security, you'll be redirected to login after changing your password.
                                                </Text>
                                            </HStack>
                                        </Box>
                                    </VStack>
                                </form>
                            </Box>
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UpdatePasswordForm